/**
 * Supabase Client Configuration
 * Production-ready setup for real-time database access
 */

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Auth operations
 */
export const authOperations = {
  signUp: (email, password) => supabase.auth.signUp({ email, password }),
  signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
  signOut: () => supabase.auth.signOut(),
  getCurrentUser: () => supabase.auth.getUser(),
  onAuthStateChange: (callback) => supabase.auth.onAuthStateChange(callback),
};

/**
 * Caterer operations
 */
export const catererOperations = {
  getCaterer: (userId) => supabase.from('caterers').select('*').eq('user_id', userId).single(),
  updateCaterer: (userId, data) => supabase.from('caterers').update(data).eq('user_id', userId),
  getCatererStats: (catererId) => supabase.from('caterer_stats').select('*').eq('caterer_id', catererId).single(),
};

/**
 * Bid operations
 */
export const bidOperations = {
  getAcceptedBids: (catererId, limit = 10) =>
    supabase
      .from('bids')
      .select('*, events(*)')
      .eq('caterer_id', catererId)
      .eq('status', 'accepted')
      .order('created_at', { ascending: false })
      .limit(limit),
  
  getBidById: (bidId) => supabase.from('bids').select('*, events(*)').eq('id', bidId).single(),
  
  updateBidStatus: (bidId, status) =>
    supabase.from('bids').update({ status, updated_at: new Date() }).eq('id', bidId),
};

/**
 * Order operations
 */
export const orderOperations = {
  getPendingOrders: (catererId, limit = 10) =>
    supabase
      .from('orders')
      .select('*, events(*)')
      .eq('caterer_id', catererId)
      .neq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(limit),
  
  getOrderById: (orderId) =>
    supabase
      .from('orders')
      .select('*, events(*), customers(*)')
      .eq('id', orderId)
      .single(),
  
  updateOrderStatus: (orderId, status) =>
    supabase.from('orders').update({ status, updated_at: new Date() }).eq('id', orderId),
};

/**
 * Menu operations
 */
export const menuOperations = {
  getCatererMenu: (catererId) =>
    supabase
      .from('menu_items')
      .select('*')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false }),
  
  getCatererMenuWithStats: (catererId) =>
    supabase
      .from('menu_items')
      .select(`
        id,
        name,
        price,
        order_count,
        total_revenue,
        avg_rating
      `)
      .eq('caterer_id', catererId)
      .order('order_count', { ascending: false }),
  
  getMenuItemById: (itemId) => supabase.from('menu_items').select('*').eq('id', itemId).single(),
  
  createMenuItem: (catererId, data) =>
    supabase.from('menu_items').insert({ caterer_id: catererId, ...data, created_at: new Date() }),
  
  updateMenuItem: (itemId, data) =>
    supabase.from('menu_items').update({ ...data, updated_at: new Date() }).eq('id', itemId),
  
  deleteMenuItem: (itemId) => supabase.from('menu_items').delete().eq('id', itemId),
};

/**
 * Availability operations
 */
export const availabilityOperations = {
  getCatererAvailability: (catererId) =>
    supabase
      .from('availability')
      .select('*')
      .eq('caterer_id', catererId)
      .order('date', { ascending: true }),
  
  blockDate: (catererId, data) =>
    supabase.from('availability_blocks').insert({
      caterer_id: catererId,
      ...data,
      created_at: new Date(),
    }),
  
  removeBlock: (blockId) => supabase.from('availability_blocks').delete().eq('id', blockId),
  
  getBlockedDates: (catererId, startDate, endDate) =>
    supabase
      .from('availability_blocks')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('date', startDate)
      .lte('date', endDate),
};

/**
 * Revenue operations
 */
export const revenueOperations = {
  getCatererRevenue: (catererId, startDate, endDate) =>
    supabase
      .from('revenue')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('created_at', startDate)
      .lte('created_at', endDate),
  
  getCatererRevenueByDateRange: (catererId, startDate, endDate) => {
    const start = startDate.toISOString().split('T')[0];
    const end = endDate.toISOString().split('T')[0];
    return supabase
      .from('revenue')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('created_at', start)
      .lte('created_at', end)
      .order('created_at', { ascending: true });
  },
  
  getPayoutHistory: (catererId, limit = 12) =>
    supabase
      .from('payouts')
      .select('*')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false })
      .limit(limit),
};

export default supabase;
